// Contatos da própria pessoa em "Meu cadastro" (`PUT /me/person/contacts`; Parte 6.5; REQ-PPL-016): o CONJUNTO inteiro é a
// unidade (substituído de uma vez, razão fixa `PPL_SELF_CONTACTS` no servidor, auditada com o ator) — sem `If-Match`.
// Validação local só do óbvio (obrigatório, e-mail, um contato principal por tipo); o resto (duplicado, telefone) é do servidor,
// e o erro de campo do servidor (`contacts[i].value`) volta ao campo certo (como nos diálogos da ficha).
import { zodResolver } from '@hookform/resolvers/zod';
import type { NamespaceKey } from '@giyus/i18n';
import { Button, Input, NativeCheckbox, NativeSelect } from '@giyus/ui';
import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ProblemAlert } from '../../../components';
import { CONTACT_KINDS, type PersonDetail, useUpdateMyContacts } from '../api/queries';
import { SERVER_MESSAGE_PREFIX, applyServerFieldErrors } from '../lib/formErrors';
import {
  type ContactsFormValues,
  contactsSchema,
  contactsToForm,
  toContactInputs,
} from '../lib/selfService';

export function MyContactsCard({ person }: { readonly person: PersonDetail }) {
  const { t } = useTranslation('people');
  const update = useUpdateMyContacts();
  const [message, setMessage] = useState<string | null>(null);
  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<ContactsFormValues>({
    resolver: zodResolver(contactsSchema),
    defaultValues: contactsToForm(person.contacts),
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'contacts' });
  const translate = (text: string | undefined): string | undefined => {
    if (!text) {
      return undefined;
    }
    if (text.startsWith(SERVER_MESSAGE_PREFIX)) {
      const code = text.slice(SERVER_MESSAGE_PREFIX.length);
      return t(`common.serverErrors.${code}`, { defaultValue: code });
    }
    return t(text as NamespaceKey<'people'>);
  };

  return (
    <section
      className="flex flex-col gap-3 rounded-card border p-4"
      aria-labelledby="my-contacts-title"
      data-testid="my-contacts"
    >
      <div>
        <h2 id="my-contacts-title" className="text-title font-semibold">
          {t('me.contacts.title')}
        </h2>
        <p className="text-body text-muted-foreground">{t('me.contacts.description')}</p>
      </div>
      <form
        className="flex flex-col gap-3"
        noValidate
        onSubmit={(event) => {
          setMessage(null);
          void handleSubmit(async (values) => {
            try {
              const saved = await update.mutateAsync(toContactInputs(values));
              reset(contactsToForm(saved.contacts));
              setMessage(t('me.contacts.saved'));
            } catch (error: unknown) {
              // O erro de campo do servidor vai ao campo; o restante fica no ProblemAlert abaixo.
              applyServerFieldErrors(error, setError);
            }
          })(event);
        }}
      >
        {fields.length === 0 && (
          <p className="text-body text-muted-foreground" data-testid="my-contacts-empty">
            {t('me.contacts.empty')}
          </p>
        )}
        <ul className="flex flex-col gap-3">
          {fields.map((field, index) => {
            const row = errors.contacts?.[index];
            return (
              <li
                key={field.id}
                className="grid items-start gap-2 sm:grid-cols-[10rem_1fr_auto_auto]"
                data-testid={`my-contact-${String(index)}`}
              >
                <NativeSelect
                  aria-label={t('me.contacts.kindLabel', { index: index + 1 })}
                  {...register(`contacts.${index}.kind`)}
                >
                  {CONTACT_KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {t(`common.contactKind.${kind}`)}
                    </option>
                  ))}
                </NativeSelect>
                <div className="flex flex-col gap-1">
                  <Input
                    aria-label={t('me.contacts.valueLabel', { index: index + 1 })}
                    aria-invalid={Boolean(row?.value)}
                    {...register(`contacts.${index}.value`)}
                  />
                  {row?.value?.message && (
                    <p role="alert" className="text-caption text-destructive">
                      {translate(row.value.message)}
                    </p>
                  )}
                  {row?.isPrimary?.message && (
                    <p role="alert" className="text-caption text-destructive">
                      {translate(row.isPrimary.message)}
                    </p>
                  )}
                </div>
                <label className="flex min-h-8 items-center gap-2 text-body">
                  <NativeCheckbox {...register(`contacts.${index}.isPrimary`)} />
                  {t('me.contacts.primary')}
                </label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    remove(index);
                  }}
                >
                  {t('me.contacts.remove', { index: index + 1 })}
                </Button>
              </li>
            );
          })}
        </ul>
        {errors.contacts?.message && (
          <p role="alert" className="text-caption text-destructive">
            {translate(errors.contacts.message)}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid="my-contacts-add"
            onClick={() => {
              append({ kind: 'Email', value: '', isPrimary: false });
            }}
          >
            {t('me.contacts.add')}
          </Button>
          <Button type="submit" disabled={update.isPending} data-testid="my-contacts-save">
            {t('me.contacts.save')}
          </Button>
          {message && (
            <p role="status" className="text-body" data-testid="my-contacts-message">
              {message}
            </p>
          )}
        </div>
        {update.isError && <ProblemAlert error={update.error} />}
      </form>
    </section>
  );
}
